<template>
  <div>
    <portal to="settings-header">
      <div class="d-flex align-center">
        <v-select
        hide-details
        small
        dense
        outlined
        color="primary"
        class="mt-1"
        :items="items"
        v-model="selectedPlan"
        :loading="loading"
    ></v-select>
        <v-btn
          large
          outlined
          color="primary"
          class="text-none"
          @click="downloadCSV"
          :class="$vuetify.breakpoint.smAndDown ? '' : 'ml-4'"
        >
          <v-icon
            left
            small
            v-text="'mdi-download-outline'"
          ></v-icon>
        {{ $t('planningCamso.downloadCsvTemplate') }}
        </v-btn>
      </div>
    </portal>
    <v-responsive :max-width="400">
      <v-file-input
        filled
        hide-details
        single-line
        ref="fileupload"
        :label="$t('planningCamso.selectFile')"
        prepend-inner-icon="mdi-upload-outline"
        prepend-icon=""
        @change="onFileChanged"
      ></v-file-input>
    </v-responsive>
    <div class="caption">
      {{ $t('planningCamso.importHint') }}
    </div>
    <div v-if="fileImported" class="mt-4">
      <div class="mb-2">
        {{ message }}
      </div>
      <review-plans
        :tags="tags"
        title="Plans"
        :records="records"
        :masterTags="masterTags"
        :reviewType="reviewType"
        :missingTags="missingTags"
        :missingData="missingData"
        :matchedColumns="matchedColumns"
        @data-reviewed="processDataReview"
        :invalidDataTypes="invalidDataTypes"
        :invalidPartMatrix="invalidPartMatrix"
        @column-reviewed="processColumnReview"
      />
    </div>
    <v-btn
      class="text-none mt-8"
      color="primary"
      :disabled="loading || error || !file"
      @click="importRecords"
    >
      {{ $t('planningCamso.import') }}
    </v-btn>
  </div>
</template>

<script>
import {
  mapState, mapActions, mapGetters, mapMutations,
} from 'vuex';
import CSVParser from '@shopworx/services/util/csv.service';
import { formatDate } from '@shopworx/services/util/date.service';
import ReviewPlans from './validate-import/ReviewPlans.vue';

export default {
  name: 'ImportPlans',
  components: {
    ReviewPlans,
  },
  data() {
    return {
      items: ['Carcass Plan', 'Curing Plan'],
      selectedPlan: 'Carcass Plan',
      fileImported: false,
      tags: null,
      file: null,
      message: '',
      error: false,
      loading: false,
      saving: false,
      records: null,
      reviewType: '',
      mappedTags: null,
      missingTags: null,
      missingData: null,
      importedRows: null,
      matchedColumns: null,
      importedColumns: null,
      invalidDataTypes: null,
      invalidPartMatrix: null,
      carcassTags: [{
        tagName: 'partnumber',
        tagDescription: 'Part number',
        required: true,
        emgTagType: 'string',
      }, {
        tagName: 'machinename',
        tagDescription: 'Machine name',
        required: true,
        emgTagType: 'string',
      }, {
        tagName: 'plannedquantity',
        tagDescription: 'Quantity',
        required: true,
        emgTagType: 'int',
      }, {
        tagName: 'scheduledstart',
        tagDescription: 'Scheduled start (DD-MM-YY HH:mm)',
        required: true,
        emgTagType: 'date',
      }],
      curingTags: [{
        tagName: 'partnumber',
        tagDescription: 'Part number',
        required: true,
        emgTagType: 'string',
      }, {
        tagName: 'machinename',
        tagDescription: 'Machine name',
        required: true,
        emgTagType: 'string',
      }, {
        tagName: 'moldnumber',
        tagDescription: 'Mould',
        required: true,
        emgTagType: 'string',
      }, {
        tagName: 'treadmachine',
        tagDescription: 'Phase',
        required: true,
        emgTagType: 'string',
      }, {
        tagName: 'plannedquantity',
        tagDescription: 'Quantity',
        required: true,
        emgTagType: 'int',
      }, {
        tagName: 'scheduledstart',
        tagDescription: 'Scheduled start (DD-MM-YY HH:mm)',
        required: true,
        emgTagType: 'date',
      }, {
        tagName: 'comments',
        tagDescription: 'Comments',
        required: false,
        emgTagType: 'string',
      }],
    };
  },
  computed: {
    ...mapGetters('planningCamso', ['partMatrixTags']),
    ...mapState('planningCamso', [
      'machines',
      'molds',
    ]),
    masterTags() {
      if (this.selectedPlan === 'Carcass Plan') {
        return this.carcassTags;
      }
      return this.curingTags;
    },
  },
  methods: {
    ...mapMutations('helper', ['setAlert']),
    ...mapActions('planningCamso', [
      'fetchMachines',
      'fetchMolds',
      'fetchPartMatrix',
      'fetchLastPlan',
      'getScheduledEnd',
      'createPlans',
      'fetchParts',
    ]),
    async importRecords() {
      this.saving = true;
      const dateTime = (rec) => {
        const [date, time] = rec.toString().split(' ');
        const [day, month, year] = date.split('-');
        const [hr, min] = time.split(':');
        const currentYear = new Date().getFullYear();
        const currentYearInitial = currentYear.toString().substring(0, 2);
        return new Date(currentYearInitial + year, month - 1, day, hr, min, 0).getTime();
      };
      const payload = await Promise.all(this.records.map(async ({ equipmentname, ...rec }) => {
        const scheduledstart = dateTime(rec.scheduledstart);
        const record = this.sanatizePayload(rec);
        return {
          ...record,
          scheduledstart,
          plandate: formatDate(scheduledstart, 'yyyyMMdd'),
          scheduledend: await this.fetchEstimatedEnd(rec, scheduledstart),
        };
      }));
      const created = await this.createPlans(payload);
      if (created) {
        this.setAlert({
          show: true,
          type: 'success',
          message: 'PLAN_IMPORTED',
        });
        this.$refs.fileupload.value = null;
        this.file = null;
        this.fileImported = false;
      } else {
        this.setAlert({
          show: true,
          type: 'error',
          message: 'ERROR_CREATING_PLAN',
        });
      }
      this.saving = false;
    },
    async fetchEstimatedEnd(plan, scheduledstart) {
      const {
        plannedquantity, stdcycletime, curingtime, handlingtime,
      } = plan;
      let runTime = 0;
      if (this.selectedPlan === 'Carcass Plan') {
        runTime = +plannedquantity * (+stdcycletime * 60 * 1000);
      } else {
        runTime = +plannedquantity * ((curingtime * 60 * 1000) + (handlingtime * 60 * 1000));
      }
      if (plannedquantity && scheduledstart) {
        return this.getScheduledEnd({
          start: scheduledstart,
          duration: runTime,
        });
      }
      return '';
    },
    generateCSV({ fields, data = [] }) {
      const csvParser = new CSVParser();
      const content = csvParser.unparse({ fields, data });
      return content;
    },
    downloadCSV() {
      const fileName = 'import-plans-template.csv';
      const fields = this.masterTags.map((t) => t.tagDescription);
      const content = this.generateCSV({ fields });
      const blob = new Blob([content]);
      if (window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveBlob(blob, fileName);
      } else {
        const a = window.document.createElement('a');
        a.href = window.URL.createObjectURL(blob, { type: 'text/plain' });
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    },
    async onFileChanged(e) {
      this.file = e && e !== undefined ? e : null;
      if (this.file) {
        this.fileImported = true;
        await this.processFileUpload();
      } else {
        this.fileImported = false;
      }
    },
    async processFileUpload() {
      this.loading = true;
      await this.mapFile();
      if (this.importedRows && this.importedRows.length
        && this.importedColumns && this.importedColumns.length) {
        this.mapColumns();
        this.validateColumns();
        if (this.mappedTags) {
          await this.mapData();
        }
      }
      this.loading = false;
    },
    async mapFile() {
      this.message = this.$i18n.t('planningCamso.setup.importMaster.parsing');
      if (this.file) {
        const csvParser = new CSVParser();
        try {
          const extractedData = await csvParser.parse(this.file);
          this.importedRows = extractedData.data;
          this.importedColumns = extractedData.meta.fields
            .map((field) => field.trim());
          if (this.importedColumns && this.importedColumns.length === 0) {
            this.error = true;
            this.file = null;
            this.message = this.$i18n.t('planningCamso.setup.importMaster.missingHeaders');
          } else if (this.importedRows && this.importedRows.length === 0) {
            this.error = true;
            this.file = null;
            this.message = this.$i18n.t('planningCamso.setup.importMaster.missingData');
          }
        } catch (e) {
          this.error = true;
          this.message = this.$i18n.t('planningCamso.setup.importMaster.fileUnparsable');
        }
      } else {
        this.error = true;
        this.message = this.$i18n.t('planningCamso.setup.importMaster.fileNotFound', { file: this.expectedFileName });
        this.message = `File ${this.expectedFileName} not found!`;
      }
    },
    mapColumns() {
      this.message = this.$i18n.t('planningCamso.setup.importMaster.mapping');
      this.matchedColumns = this.importedColumns.map((column) => {
        const matchedTag = this.masterTags.find((tag) => tag.tagDescription === column);
        let tagName = null;
        if (matchedTag) {
          ({ tagName } = matchedTag);
        }
        return {
          column,
          tagName,
          ignore: !tagName,
        };
      });
      this.validateColumns();
    },
    validateColumns() {
      const matchedTagNames = this.matchedColumns.map((col) => col.tagName);
      const unmatchedColumns = this.masterTags
        .filter((tag) => !matchedTagNames.includes(tag.tagName));
      const matchedTags = Object.values(this.mapTags());
      const uniqueTags = [...new Set(matchedTags)];
      this.missingTags = unmatchedColumns
        .filter((tag) => tag.required)
        .map((tag) => tag.tagDescription);
      if (this.missingTags && this.missingTags.length) {
        this.error = true;
        this.reviewType = 'column';
        this.message = `Missing required columns -${this.missingTags[0]}
        ${this.missingTags.length > 1 ? 'and more' : ''}!`;
      } else if (matchedTags.length !== uniqueTags.length) {
        this.error = true;
        this.reviewType = 'column';
        this.message = 'Column names are not unique!';
      } else {
        this.mappedTags = this.mapTags();
      }
    },
    mapTags() {
      return this.matchedColumns
        .filter((item) => !item.ignore && item.tagName)
        .reduce((acc, cur) => {
          acc[cur.column] = cur.tagName;
          return acc;
        }, {});
    },
    async mapData() {
      const filteredData = this.importedRows.map((row) => this.filterData(row));
      this.records = filteredData.map((data) => this.mapRow(data));
      this.tags = this.masterTags
        .filter((tag) => Object.values(this.mappedTags).includes(tag.tagName));
      await this.validateData();
    },
    filterData(obj) {
      return Object.keys(this.mappedTags)
        .map((k) => (k in obj ? { [k]: obj[k] } : null))
        .reduce((res, o) => Object.assign(res, o), {});
    },
    mapRow(obj) {
      return Object.keys(obj).reduce((acc, key) => ({
        ...acc,
        ...{ [this.mappedTags[key] || key]: obj[key] },
      }), {});
    },
    async validateData() {
      this.missingData = this.missingRequiredData();
      this.invalidDataTypes = this.validateDataType();
      this.invalidPartMatrix = await this.validatePartMatrix();
      if (this.missingData && this.missingData.length) {
        this.error = true;
        this.reviewType = 'data';
        this.message = `Missing required data for ${this.missingData[0].tag}
          at row ${this.missingData[0].row} ${this.missingData.length > 1 ? 'and more' : ''}!`;
      } else if (this.invalidDataTypes && this.invalidDataTypes.length) {
        this.error = true;
        this.reviewType = 'data';
        this.message = `Invalid data type for ${this.invalidDataTypes[0]}
          ${this.invalidDataTypes.length > 1 ? 'and more' : ''}!`;
      } else if (this.invalidPartMatrix && this.invalidPartMatrix.length) {
        this.error = true;
        this.reviewType = 'data';
        this.message = `Invalid part matrix at row ${this.invalidPartMatrix[0].row}
          ${this.invalidPartMatrix.length > 1 ? 'and more' : ''}!`;
      } else {
        this.message = 'Data successfully reviewed!';
      }
    },
    missingRequiredData() {
      const res = [];
      this.records.forEach((d, r) => {
        this.tags.forEach((t) => {
          if (t.required) {
            const val = d[t.tagName];
            if (val === null || val === '' || val === undefined) {
              res.push({ row: r + 1, tag: t.tagDescription });
            }
          }
        });
      });
      return res;
    },
    async setSortIndex() {
      const lastPlan = await this.fetchLastPlan();
      if (lastPlan) {
        return lastPlan.sortindex + 100;
      }
      return 100;
    },
    async validatePartMatrix() {
      const res = [];
      const parts = await this.fetchParts();
      await this.fetchMachines();
      await this.fetchMolds();
      await Promise.all(this.records.map(async (rec, index) => {
        const partMatrixList = await this.fetchPartMatrix({ partnumber: rec.partnumber });
        if (partMatrixList && partMatrixList.length) {
          const selectedMatrix = partMatrixList.find((m) => (
            m.partnumber === rec.partnumber
            && m.machinename === rec.machinename
          ));
          if (selectedMatrix) {
            const part = parts.find((p) => p.partnumber === rec.partnumber);
            const machine = this.machines.find((m) => m.machinename === rec.machinename);
            const mold = this.molds.find((m) => m.moldnumber === rec.moldnumber);
            if (mold) {
              delete mold._id;
            }
            // eslint-disable-next-line
            delete part._id;
            delete machine._id;
            let sortindex = 0;
            if (index === 0) {
              sortindex = await this.setSortIndex();
            } else {
              ({ sortindex } = this.records[index - 1]);
            }
            if (this.selectedPlan === 'Carcass Plan') {
              this.records[index] = {
                ...rec,
                ...part,
                ...machine,
                sortindex,
                assetid: selectedMatrix.assetid,
                stdcycletime: +selectedMatrix.stdcycletime,
                starred: false,
                trial: false,
                status: 'notStarted',
              };
            } else {
              this.records[index] = {
                ...rec,
                ...part,
                ...machine,
                ...mold,
                sortindex,
                assetid: selectedMatrix.assetid,
                curingtime: +selectedMatrix.curingtime,
                handlingtime: +selectedMatrix.handlingtime,
                starred: false,
                trial: false,
                status: 'notStarted',
              };
            }
          } else {
            res.push({ row: index + 1 });
          }
        } else {
          res.push({ row: index + 1 });
        }
      }));
      return res;
    },
    validateDataType() {
      const res = [];
      this.tags.forEach((t) => {
        const matchedRecords = this.records.map((rec) => rec[t.tagName]);
        if (
          t.emgTagType.toLowerCase() === 'int'
          || t.emgTagType.toLowerCase() === 'long'
          || t.emgTagType.toLowerCase() === 'duration'
        ) {
          const invalid = matchedRecords.some((rec) => rec % 1 !== 0);
          if (invalid) {
            res.push(`${t.tagDescription}(Number)`);
          }
        } else if (
          t.emgTagType.toLowerCase() === 'float'
          || t.emgTagType.toLowerCase() === 'double'
        ) {
          const invalid = matchedRecords.some((rec) => Number.isNaN(rec - parseFloat(rec)));
          if (invalid) {
            res.push(`${t.tagDescription}(Decimal)`);
          }
        } else if (t.emgTagType.toLowerCase() === 'boolean') {
          const truthyValues = ['true', 'false', true, false];
          const invalid = matchedRecords.some((rec) => {
            if (rec) {
              return !truthyValues.includes(rec.toLowerCase());
            }
            return true;
          });
          if (invalid) {
            res.push(`${t.tagDescription}(Boolean)`);
          }
        } else if (t.emgTagType.toLowerCase() === 'date') {
          const isDateTime = (rec) => {
            const [date, time] = rec.toString().split(' ');
            if (date && time) {
              const [day, month, year] = date.split('-');
              const [hr, min] = time.split(':');
              return new Date(year, month - 1, day, hr, min, 0).toString() !== 'Invalid Date';
            }
            return false;
          };
          const invalid = matchedRecords.some((rec) => !isDateTime(rec));
          if (invalid) {
            res.push(`${t.tagDescription}`);
          }
        }
      });
      return res;
    },
    sanatizePayload(payload) {
      // eslint-disable-next-line
      delete payload._id;
      delete payload.questions;
      delete payload.elementId;
      delete payload.customerId;
      delete payload.siteId;
      delete payload.userId;
      delete payload.modifiedtimestamp;
      delete payload.createdTimestamp;
      return payload;
    },
    async processColumnReview(matchedColumns) {
      this.loading = true;
      this.matchedColumns = matchedColumns;
      this.validateColumns();
      if (this.mappedTags) {
        await this.mapData();
      }
      this.loading = false;
    },
    async processDataReview(records) {
      this.loading = true;
      this.records = records;
      await this.validateData();
      this.loading = false;
    },
  },
};
</script>
